import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { applySX, Show, type WithSX } from '@elfsight/lib-utils';
import { useEmitAPIEvent } from '@elfsight/widget-js-api';
import { createReadableClass } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import { API_INIT_EVENT } from '../../constants';
import { isInlineLayout } from '../../utils';
import { useCountdownTimerState } from '../countdown-timer-state-context';
import { ElementsProvider } from '../elements';
import { MEDIUM_BREAKPOINT, MOBILE_BREAKPOINT } from './constants';
import { LayoutContext } from './layout-context';
import { useBreakpoints } from './utils';
import { LayoutBar, LayoutInline } from './variants';

export function LayoutInner() {
  const settings = useSettings();
  const { layout } = settings;

  const { state } = useCountdownTimerState();

  const refContainer = useRef<HTMLDivElement | null>(null);

  const isInitializedRef = useRef(false);
  const emitAPIEvent = useEmitAPIEvent();
  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    // setTimeout - костыль, убрать после релиза https://elfsight.atlassian.net/browse/WIDGETS-2999
    setTimeout(() =>
      emitAPIEvent(API_INIT_EVENT, [
        { settings, widgetContainer: refContainer.current }
      ])
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { ref, breakpoints } = useBreakpoints();

  const isInline = isInlineLayout(layout);

  const mergedRef = useMergedRef(refContainer, ref);

  const isHidden = state === 'hidden';
  const containerLayoutClass = createReadableClass({
    component: 'layout-inner',
    element: 'container',
    modifier: layout
  });
  const containerClass = createReadableClass(
    {
      component: 'layout-inner',
      element: 'container',
      index: isHidden && 'hidden'
    },
    [containerLayoutClass]
  );

  const [isMobileBreakpoint, isMediumBreakpoint] = [
    breakpoints[MOBILE_BREAKPOINT],
    breakpoints[MEDIUM_BREAKPOINT]
  ];

  const context = useMemo(
    () => ({
      isMobileBreakpoint,
      isMediumBreakpoint
    }),
    [isMobileBreakpoint, isMediumBreakpoint]
  );

  return (
    <LayoutContext.Provider value={context}>
      <Container
        className={containerClass}
        $sx={{
          display: isHidden ? 'none' : 'flex'
        }}
      >
        <ElementsProvider>
          <Show when={isInline} fallback={<LayoutBar ref={mergedRef} />}>
            <LayoutInline ref={mergedRef} />
          </Show>
        </ElementsProvider>
      </Container>
    </LayoutContext.Provider>
  );
}

const Container = styled.div<WithSX>`
  ${applySX};
  width: 100%;
`;
